import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

// ── Sub-types (mirrored from echoes/frontend PassportContext) ──────────────

export interface ProServiceReport {
  reportNumber: string | null;
  startDate: string | null;
  daysAgo: number | null;
  technicianName: string | null;
  customerName: string | null;
  machineSerialNumber: string | null;
  machineDescription: string | null;
  problemDescription: string | null;
  workDescription: string | null;
  futureWorkDescription: string | null;
}

export interface AlarmEntry {
  description: string;
  count: number;
}

export interface FabricodeAlarmReport {
  last7DaysTotal: number;
  historicalWeeklyAverage: number;
  historicalWeeksAnalysed: number;
  anomalyRatio: number;
  isAnomaly: boolean;
  alarmBreakdown: AlarmEntry[];
}

export interface FabricodeMachineData {
  id?: number;
  serialNumber?: string;
  name?: string;
  customerId?: number;
  function?: string;
  operator?: string;
  year?: string;
  machineType?: string;
}

export interface FabricodeMatch {
  machineId: number;
  serialNumber: string;
  description: string | null;
  alarmReport: FabricodeAlarmReport | null;
}

export interface ZendeskAttachment {
  id: number;
  file_name: string;
  content_url: string;
  content_type: string;
  size: number;
}

export interface ZendeskComment {
  id: number;
  body?: string;
  author_id?: number;
  public?: boolean;
  created_at?: string;
  attachments?: ZendeskAttachment[];
}

export interface ZendeskTicket {
  id: number;
  subject?: string;
  status?: string;
  priority?: string;
  created_at?: string;
  updated_at?: string;
  description?: string;
  machineSerialNumber?: string | null;
  comments?: ZendeskComment[];
}

// ── Main result type ───────────────────────────────────────────────────────

export interface PassportResult {
  serialNumber: string;
  machinePassportText: string;
  proservice: { reports: ProServiceReport[] } | null;
  fabricode: {
    machine: FabricodeMachineData | null;
    alarmReport: FabricodeAlarmReport | null;
    notFound?: boolean;
    multipleMatches?: FabricodeMatch[];
  } | null;
  zendesk: { tickets: ZendeskTicket[] } | null;
}

// ── Fetch ──────────────────────────────────────────────────────────────────

export async function fetchPassport(serialNumber: string): Promise<PassportResult> {
  const sn = serialNumber.trim();
  const authHeader = authService.getAuthorizationHeader();
  const headers: Record<string, string> = {};
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }

  const response = await fetch(
    `${API_BASE_URL}/passport/${encodeURIComponent(sn)}`,
    {
      headers,
      signal: AbortSignal.timeout(30000),
    },
  );

  if (!response.ok) {
    let message = `Errore ${response.status}`;
    try {
      const data = await response.json();
      if (data?.message) message = data.message;
      else if (data?.error?.message) message = data.error.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  return response.json();
}
